import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Box,
  Typography,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  CircularProgress
} from '@mui/material';
import { 
  CloudUpload as UploadIcon, 
  Attachment as AttachmentIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { createPost, updatePost, uploadAttachment, deleteAttachment } from '../../api/news';
import adminApi from '../../api/admin';
import MarkdownEditor from './MarkdownEditor';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const NewsPostDialog = ({ open, onClose, post = null }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    role_ids: [],
    is_pinned: false
  });
  const [error, setError] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  // Fetch roles from API
  const fetchRoles = async () => {
    try {
      setRolesLoading(true);
      const fetchedRoles = await adminApi.getRoles();
      setRoles(fetchedRoles);
    } catch (err) {
      console.error('Error fetching roles:', err);
      setError('Failed to load roles. Please try again.');
    } finally {
      setRolesLoading(false);
    }
  };

  // Initialize form when opening dialog
  useEffect(() => {
    if (open) {
      // Fetch roles when dialog opens
      fetchRoles();
      
      if (post) {
        // Editing existing post
        setFormData({
          title: post.title,
          content: post.content,
          role_ids: post.roles.map(r => r.id),
          is_pinned: post.is_pinned
        });
        setAttachments(post.attachments || []);
      } else {
        // Creating new post
        setFormData({
          title: '',
          content: '',
          role_ids: [],
          is_pinned: false
        });
        setAttachments([]);
      }
      setError(null);
    }
  }, [open, post]);

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'is_pinned' ? checked : value,
    });
  };

  const handleRoleChange = (event) => {
    const { value } = event.target;
    setFormData({
      ...formData,
      role_ids: value,
    });
  };

  const handleContentChange = (value) => {
    setFormData({
      ...formData,
      content: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      console.log("Sending news post data:", formData);
      
      if (post) {
        // Update existing post
        console.log(`Updating post ID ${post.id}`);
        const updatedPost = await updatePost(post.id, formData);
        console.log("Post updated successfully:", updatedPost);
      } else {
        // Create new post
        console.log("Creating new post");
        const newPost = await createPost(formData);
        console.log("Post created successfully:", newPost);
      }
      onClose(true); // Refresh posts on parent component
    } catch (err) {
      console.error('Error saving post:', err);
      if (err.response) {
        console.error('Error response:', err.response.data);
        setError(`Failed to save post: ${err.response.data.detail || 'Unknown server error'}`);
      } else if (err.request) {
        console.error('No response received:', err.request);
        setError('Failed to save post: No response from server');
      } else {
        setError(`Failed to save post: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !post) return;

    setUploadProgress(true);
    try {
      const result = await uploadAttachment(post.id, file);
      setAttachments([...attachments, result]);
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Failed to upload attachment');
    } finally {
      setUploadProgress(false);
      // Clear the input
      e.target.value = null;
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!post) return;

    try {
      await deleteAttachment(post.id, attachmentId);
      setAttachments(attachments.filter(att => att.id !== attachmentId));
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setError('Failed to delete attachment');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => onClose(false)}
      maxWidth="md"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {post ? 'Edit News Post' : 'Create News Post'}
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          
          <TextField
            name="title"
            label="Title"
            value={formData.title}
            onChange={handleInputChange}
            fullWidth
            required
            margin="normal"
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="roles-label">Roles with Access</InputLabel>
            <Select
              labelId="roles-label"
              multiple
              name="role_ids"
              value={formData.role_ids}
              onChange={handleRoleChange}
              input={<OutlinedInput label="Roles with Access" />}
              renderValue={(selected) => {
                const roleNames = selected.map(id => {
                  const role = roles.find(r => r.id === id);
                  return role ? role.name : '';
                }).filter(Boolean);
                return roleNames.join(', ');
              }}
              MenuProps={MenuProps}
              required
              disabled={rolesLoading}
            >
              {rolesLoading ? (
                <MenuItem disabled>
                  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                </MenuItem>
              ) : roles.length > 0 ? (
                roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Checkbox checked={formData.role_ids.indexOf(role.id) > -1} />
                    <ListItemText primary={role.name} />
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>
                  <ListItemText primary="No roles available" />
                </MenuItem>
              )}
            </Select>
          </FormControl>
          
          <FormControlLabel
            control={
              <Checkbox 
                checked={formData.is_pinned}
                onChange={handleInputChange}
                name="is_pinned"
              />
            }
            label="Pin this post at the top of the news feed"
            sx={{ mt: 1 }}
          />
          
          <Box mt={2}>
            <Typography variant="h6" gutterBottom>
              Content (Markdown supported)
            </Typography>
            <MarkdownEditor
              value={formData.content}
              onChange={handleContentChange}
            />
          </Box>
          
          {post && (
            <>
              <Divider sx={{ mt: 3, mb: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                Attachments
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <input
                  type="file"
                  id="attachment-upload"
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
                <label htmlFor="attachment-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    disabled={uploadProgress}
                  >
                    {uploadProgress ? 'Uploading...' : 'Upload Attachment'}
                  </Button>
                </label>
              </Box>
              
              {attachments.length > 0 ? (
                <List>
                  {attachments.map((attachment) => (
                    <ListItem key={attachment.id}>
                      <ListItemIcon>
                        <AttachmentIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary={attachment.filename} 
                        secondary={`${(attachment.file_size / 1024).toFixed(1)} KB`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          onClick={() => handleDeleteAttachment(attachment.id)}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="textSecondary">
                  No attachments yet
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : (post ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default NewsPostDialog;